import classNames from "classnames";
import React, { useEffect, useState, useMemo } from 'react';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import {
    Card,
    CardBody,
} from "reactstrap";

const AbsentReactTable = ({ columns, data, filters, school, tableClassName }) => {



    if (school !== null) {
        let index_class = 1;
        school.forEach((value, i) => {
            index_class = (i % 12) === 0 && i >= 12 ? 1 : index_class;
            school[i].index = index_class;
            index_class += 1;
        });
    }

    if (data !== null) {
        data.forEach((dataRow, i1) => {
            school.forEach((dataSchool) => {
                if (dataSchool.id === dataRow.iSchoolId) {
                    data[i1].index = dataSchool.index;
                }
            });
        });
    }

    const tableClass = tableClassName || "mb-0 table table-sm table-hover";
    const { search = true, columnFilter = false, pagination = true, sorting = true } = filters || {};

    const [globalFilter, setGlobalFilter] = useState('');
    const [pageSize, setPageSize] = useState(10000);

    const filterFns = useMemo(() => ({
        text: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return value !== undefined
                ? String(value).toLowerCase().startsWith(String(filterValue).toLowerCase())
                : true;
        },
    }), []);

    const table = useReactTable({
        data,
        columns,
        filterFns,
        state: {
            globalFilter,
        },
        globalFilterFn: 'text',
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        ...(sorting && { getSortedRowModel: getSortedRowModel() }),
        ...(pagination && { getPaginationRowModel: getPaginationRowModel() }),
    });

    useEffect(() => {
        table.setPageSize(pageSize);
    }, [pageSize]);

    return (
        <>
            {search && (
                <div className="form-group pull-right mb-0">
                    <input
                        type="text"
                        value={globalFilter ?? ""}
                        className="form-control mb-2 search"
                        placeholder="Search ... "
                        onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                </div>
            )}
            <div className='table-wrapper'>
                <table className={tableClass}>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup, id) => (
                            <tr key={headerGroup.id} className="absent-table-tr text-center">
                                {headerGroup.headers.map(header => (
                                    <th colSpan={header.colSpan} key={header.id} className={classNames({ "text-center": id === 0 })}>
                                        {header.isPlaceholder ? null : (
                                            <span
                                                key={
                                                    typeof header.column.columnDef.header === "string"
                                                        ? header.column.columnDef.header
                                                        : header.id
                                                }
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                            </span>
                                        )}
                                        {sorting && header.column.getCanSort() && (
                                            <span>
                                                {{
                                                    asc: ' 🔼',
                                                    desc: ' 🔽',
                                                }[header.column.getIsSorted()] ?? ''}
                                            </span>
                                        )}
                                        <div>
                                            {header.column.getCanFilter() && columnFilter
                                                ? flexRender(header.column.columnDef.filterFn, header.getContext())
                                                : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row, i) => (
                            <tr
                                colSpan={row.colSpan}
                                key={row.id}
                                data={data[i].index}
                                className={`row_color-${data[i].index} ${i % 2 !== 0 ? "odd-color" : ""}`}
                            >
                                {row.getVisibleCells().map((cell, id) => {
                                    const cellValue = cell.getValue();
                                    const backgroundColor =
                                        cellValue && cellValue.length === 1 && cellValue[0].vColor
                                            ? `#${cellValue[0].vColor}`
                                            : cellValue && cellValue.length > 1
                                                ? '#ff6961'
                                                : '';

                                    return (
                                        <td
                                            key={cell.id}
                                            className={cellValue !== null && cellValue !== undefined && id > 2 ? "cell-color-style" : ""}
                                            style={{
                                                backgroundColor: cell.column.id.includes("middag") || cell.column.id.includes("ochtend")
                                                    ? backgroundColor
                                                    : '',
                                            }}
                                        >
                                            <span key={JSON.stringify(cellValue)}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </span>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {pagination && (
                <Card className="main-card mb-3">
                    <CardBody className="d-flex flex-wrap justify-content-end align-items-start">
                        <form className="d-flex justify-content-end me-2 h-75">
                            <div className="form-group col-md-4">
                                <div className="input-group input-group-sm">
                                    <div className="input-group-text font-icon-wrapper font-icon">
                                        <i className="pe-7s-file"> </i>
                                    </div>
                                    <input
                                        className="form-control form-control-sm me-2"
                                        type="number"
                                        min="0"
                                        defaultValue={table.getState().pagination.pageIndex + 1}
                                        onChange={e => {
                                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                            table.setPageIndex(page);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="form-group col-md-3">
                                <select
                                    className="form-select form-select-sm"
                                    value={table.getState().pagination.pageSize}
                                    onChange={e => table.setPageSize(Number(e.target.value))}
                                >
                                    {[10, 20, 30, 50, 10000].map(pageSize => (
                                        <option key={pageSize} value={pageSize}>
                                            Show {pageSize}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </form>

                        <nav aria-label="Table navigation" className="nav">
                            <ul className="pagination">
                                <li>
                                    <a className="page-link">
                                        Page{' '}
                                        <strong>
                                            {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                                        </strong>{' '}
                                    </a>
                                </li>
                                <li className="page-item" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                                    <a className="page-link">First</a>
                                </li>
                                <li className="page-item" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                                    <a className="page-link">{'<'}</a>
                                </li>
                                <li className="page-item" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                                    <a className="page-link">{'>'}</a>
                                </li>
                                <li className="page-item" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
                                    <a className="page-link">Last</a>
                                </li>
                            </ul>
                        </nav>
                    </CardBody>
                </Card>
            )}
        </>
    );
};

export default AbsentReactTable;
